<template>
  <div class="social-media-dialog text-center">
    <v-dialog
      v-model="dialog"
      :persistent="persistent"
      width="500"
      :fullscreen="$vuetify.display.smAndDown"
    >
      <v-card class="social-media-dialog d-flex flex-column">
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <div class="d-flex w-100">
              <v-toolbar-title class="ml-6">
                <template v-if="step === 1">
                  <span v-if="content?.textKind === postType.newsletter">
                    {{ $t('content-creator.delete-scheduled-emails.title') }}
                  </span>
                  <span v-else>{{ $t('content-creator.delete-post.title') }}</span>
                </template>
                <template v-else>
                  <div class="d-flex align-center">
                    <span v-if="content?.textKind === postType.newsletter">
                      {{ $t('content-creator.delete-scheduled-emails.title') }}
                    </span>
                    <span v-else>{{ $t('content-creator.delete-post.title') }}</span>
                  </div>
                </template>
              </v-toolbar-title>
            </div>
            <v-toolbar-items class="ml-3">
              <v-btn
                icon
                @click="$emit('close')"
              >
                <v-icon size="large"> mdi-close </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-stepper
          v-model="step"
          alt-labels
          flat
          color="primary"
          elevation="0"
        >
          <v-stepper-window v-model="step">
            <v-stepper-window-item
              :value="1"
              class="stepper"
              :class="{ 'pa-0': content.textKind === postType.newsletter }"
            >
              <v-card-text class="pa-0 card-text">
                <h4 class="content-title">
                  <span v-if="content.textKind === postType.newsletter">
                    {{ $t('content-creator.delete-scheduled-emails.content.0') }}
                  </span>
                  <span v-else>{{ $t('content-creator.delete-post.content.0') }}</span>
                </h4>
                <span class="content-subtitle">{{
                  $t('content-creator.delete-post.content.1')
                }}</span>
                <div v-if="content.textKind === postType.newsletter">
                  <ScheduledPostItem
                    :post="content"
                    :display-checkbox="false"
                    :is-post-type-newsletter="true"
                  />
                </div>
                <template v-else>
                  <div
                    v-for="post in socialMediaPosts"
                    :key="post.id"
                    class="d-flex justify-start align-center px-2"
                  >
                    <ScheduledPostItem
                      :post="post"
                      :display-checkbox="hasMoreThanOnePost"
                      @update:post="updatePost"
                    />
                  </div>
                  <div
                    v-if="hasMoreThanOnePost"
                    class="d-flex align-center px-2 mt-3"
                  >
                    <v-checkbox
                      :model-value="allPostsSelected"
                      :indeterminate="hasSelectedPost && !allPostsSelected"
                      color="primary"
                      hide-details
                      class="ma-0 pa-0"
                      :label="$t('content-creator.delete-post.select-all')"
                      @update:model-value="toggleSelectAll"
                    />
                  </div>
                </template>
              </v-card-text>
              <v-card-actions class="d-flex justify-space-between pa-0 card-actions">
                <v-btn
                  class="px-0 btn close-btn"
                  variant="text"
                  @click="$emit('close')"
                >
                  {{ $t('labels.close') }}
                </v-btn>

                <v-btn
                  v-if="content?.textKind === postType.newsletter"
                  color="primary"
                  class="px-0 btn scheduled-post-btn"
                  variant="text"
                  :loading="loading"
                  :disabled="!content.scheduledAt"
                  @click="deleteInviteLeadToLandingpage()"
                >
                  {{ $t('buttons.content-creator.scheduled-post.delete') }}
                </v-btn>

                <v-btn
                  v-else
                  color="primary"
                  class="px-0 btn scheduled-post-btn"
                  variant="text"
                  :loading="loading"
                  :disabled="!hasSelectedPost"
                  @click="deleteSocialMediaPosts()"
                >
                  {{ $t('buttons.content-creator.scheduled-post.delete') }}
                </v-btn>
              </v-card-actions>
            </v-stepper-window-item>

            <v-stepper-window-item
              :value="2"
              class="py-0"
            >
              <DeleteScheduledPostSuccess @close="$emit('close')" />
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType.js'
import DELETE_INVITE_LEAD_TO_LANDINGPAGE from '@/modules/contentCreator/creator/queries/deleteInviteLeadToLandingpage.gql'
import DELETE_SAVED_CONTENT from '@/modules/contentCreator/creator/queries/deleteSavedContent.gql'
import DELETE_SAVED_CONTENT_BATCH from '@/modules/contentCreator/creator/queries/deleteSavedContentBatch.gql'
import DELETE_SAVED_CONTENT_FOR_NEWSLETTER from '@/modules/contentCreator/creator/queries/deleteSavedContentForNewsletter.gql'
import DELETE_SOCIAL_MEDIA_POSTS from '@/modules/contentCreator/creator/queries/deleteSocialMediaPosts.gql'
import DeleteScheduledPostSuccess from '@/modules/contentCreator/creator/SocialMedia/DeleteScheduledPostSuccess.vue'
import ScheduledPostItem from '@/modules/contentCreator/creator/SocialMedia/ScheduledPostItem.vue'

export default {
  components: {
    ScheduledPostItem,
    DeleteScheduledPostSuccess,
  },
  emits: ['close', 'postDeleted', 'scheduledEmailsDeleted'],
  props: {
    content: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      socialMediaPosts: (this.content?.socialMediaPosts || []).map((post) => ({
        ...post,
        selected: (this.content?.socialMediaPosts || []).length <= 1,
      })),
      dialog: true,
      persistent: true,
      loading: false,
      step: 1,
    }
  },
  computed: {
    postType() {
      return PostType
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    hasMoreThanOnePost() {
      return this.socialMediaPosts.length > 1
    },
    hasSelectedPost() {
      return this.socialMediaPosts.some((post) => post.selected)
    },
    allPostsSelected() {
      return this.socialMediaPosts.every((post) => post.selected)
    },
  },
  methods: {
    async deleteInviteLeadToLandingpage() {
      try {
        this.loading = true
        await this.$apollo.mutate({
          mutation: DELETE_INVITE_LEAD_TO_LANDINGPAGE,
          variables: {
            input: {
              savedContentId: this.content.id,
            },
          },
        })

        await this.deleteSavedContent()
        this.$tracking.event('Content Creator', 'Edited', 'Deleted Scheduled Emails')
        this.$emit('scheduledEmailsDeleted')
      } catch (err) {
        showSnackbarMessage('error', 'An error occurred while deleting scheduled emails.')
      } finally {
        this.loading = false
      }
    },
    updatePost(updatedPost) {
      const postIndex = this.socialMediaPosts.findIndex((p) => p.id === updatedPost.id)
      if (postIndex !== -1) {
        this.socialMediaPosts[postIndex] = { ...updatedPost }
      }
    },
    toggleSelectAll(value) {
      this.socialMediaPosts = this.socialMediaPosts.map((post) => ({
        ...post,
        selected: value,
      }))
    },
    async deleteSocialMediaPosts() {
      if (!this.hasSelectedPost) return

      this.loading = true

      try {
        if (this.isMasterView && this.content?.postingBatchId) {
          if (this.allPostsSelected) {
            await this.deleteSavedContent()
          } else {
            const selectedTypes = new Set(
              this.socialMediaPosts.filter((p) => p.selected).map((p) => p.type)
            )
            const ids = (this.content.posts || [])
              .flatMap((post) => post.socialMediaPosts || [])
              .filter((smp) => selectedTypes.has(smp.type))
              .map((smp) => smp.id)

            await this.$apollo.mutate({
              mutation: DELETE_SOCIAL_MEDIA_POSTS,
              variables: {
                input: { ids },
              },
            })
            this.$tracking.event('Content Creator', 'Edited', 'Deleted Social Media Posts')
          }
        } else {
          const selectedPosts = this.socialMediaPosts.filter((post) => post.selected)
          await this.$apollo.mutate({
            mutation: DELETE_SOCIAL_MEDIA_POSTS,
            variables: {
              input: {
                ids: selectedPosts.map((post) => post.id),
              },
            },
          })
          this.$tracking.event('Content Creator', 'Edited', 'Deleted Social Media Posts')

          if (this.allPostsSelected) {
            await this.deleteSavedContent()
          }
        }

        this.step = 2
        this.$emit('postDeleted')
      } catch (err) {
        showSnackbarMessage('error', this.$t('social-media.alert.post-delete-error'))
      } finally {
        this.loading = false
      }
    },

    async deleteSavedContent() {
      if (!this.content?.id) return

      try {
        this.loading = true
        if (this.isMasterView && this.content?.postingBatchId) {
          await this.$apollo.mutate({
            mutation: DELETE_SAVED_CONTENT_BATCH,
            variables: {
              input: {
                postingBatchId: this.content.postingBatchId,
              },
            },
          })
          return
        }

        await this.$apollo.mutate({
          mutation:
            this.content?.textKind === this.postType.newsletter
              ? DELETE_SAVED_CONTENT_FOR_NEWSLETTER
              : DELETE_SAVED_CONTENT,
          variables: {
            input: {
              id: this.content.id,
            },
          },
        })
      } catch (err) {
        showSnackbarMessage('error', this.$t('social-media.alert.saved-content-delete-error'))
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style>
.stepper {
  padding: 24px;
}

@media (max-width: 600px) {
  .stepper {
    padding: 16px;
  }
  .btn {
    width: 50%;
  }

  .delete-success {
    height: 100vh;
    align-content: center;
  }
}

.card-actions {
  margin-top: 24px;
}

.content-title {
  color: #102a21;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
}

.content-subtitle {
  color: #414142;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}
.scheduled-post-btn,
.close-btn {
  font-size: 14px !important;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
</style>
